import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Pagination,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ChartIcon from "@mui/icons-material/InsertChartOutlined";
import TableRowsIcon from "@mui/icons-material/TableRowsOutlined";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import {
  useAddExpenseMutation,
  useGetExpensesQuery,
} from "../../redux/logistics/logisticsApi";
import { useStatus } from "../../popup/NotificationContext";

const EXPENSE_TYPES = ["Fuel", "Food", "Service", "Tools", "Parts", "Hotel", "Toll", "Other"];
const PAYMENT_MODES = ["Cash", "Card", "Online"];
const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#fff",
  },
};

const toYmd = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfMonthYmd = () => {
  const now = new Date();
  return `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}-01`;
};

const parseExpenses = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.expenses)) return payload.expenses;
  return [];
};

const parseDevices = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeVehicleId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value?._id || value?.id || "");
  return String(value);
};

const getVehicleLabel = (vehicle = {}, expense = {}) =>
  vehicle?.vehicleNumber ||
  vehicle?.registrationNo ||
  vehicle?.vehicleRegistration ||
  vehicle?.name ||
  vehicle?.vehicleName ||
  expense?.vehicleNumber ||
  expense?.vehicleNumberLabel ||
  expense?.vehicleLabel ||
  expense?.vehicleName ||
  vehicle?.deviceId ||
  vehicle?.imei ||
  "Vehicle";

const normalizeExpenseRow = (expense, deviceLookup = new Map()) => {
  const expenseVehicle = expense?.vehicle || {};
  const lookupVehicleId =
    normalizeVehicleId(expenseVehicle?._id || expenseVehicle?.id) ||
    normalizeVehicleId(expense?.vehicle);
  const linkedVehicle = deviceLookup.get(lookupVehicleId) || null;
  const vehicle = linkedVehicle || expenseVehicle;
  const amount = Number(expense?.amount || 0);
  const quantity = Number(expense?.quantity || 0);
  return {
    id: expense?._id,
    date: expense?.date || expense?.createdAt,
    dateLabel: expense?.date ? new Date(expense.date).toLocaleString("en-GB") : "-",
    vehicleId: lookupVehicleId,
    vehicleNo: getVehicleLabel(vehicle, expense),
    odometer: Number(vehicle?.totalDistance || 0),
    type: expense?.type || "-",
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : "",
    mileage: "",
    model: vehicle?.model || "-",
    amount,
    average: quantity > 0 ? Number((amount / quantity).toFixed(2)) : 0,
    paymentMode: expense?.paymentMode || "-",
    description: expense?.description || "-",
    image: expense?.receiptPath || "",
    createdBy: expense?.createdBy?.username || "",
  };
};

const buildCsv = (rows) => {
  const headers = [
    "Date",
    "Vehicle no",
    "Odometer",
    "Type",
    "Qty",
    "Mileage",
    "Model",
    "Amount",
    "Average",
    "Payment mode",
    "Description",
    "Image",
  ];
  const lines = rows.map((row) =>
    [
      row.dateLabel,
      row.vehicleNo,
      row.odometer,
      row.type,
      row.quantity,
      row.mileage,
      row.model,
      row.amount,
      row.average,
      row.paymentMode,
      row.description,
      row.image,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...lines].join("\n");
};

const ExpensePortalModal = ({ open = true, onClose }) => {
  const { showStatus } = useStatus();
  const { data: expensesPayload, refetch } = useGetExpensesQuery();
  const { data: devicesPayload } = useGetAllLiteDevicesQuery();
  const [addExpense, { isLoading: isSaving }] = useAddExpenseMutation();

  const devices = useMemo(() => parseDevices(devicesPayload), [devicesPayload]);
  const deviceLookup = useMemo(() => {
    const next = new Map();
    devices.forEach((device) => {
      const key = normalizeVehicleId(device?._id || device?.id);
      if (!key) return;
      next.set(key, device);
    });
    return next;
  }, [devices]);
  const expenses = useMemo(
    () => parseExpenses(expensesPayload).map((expense) => normalizeExpenseRow(expense, deviceLookup)),
    [deviceLookup, expensesPayload]
  );

  const [mode, setMode] = useState("table");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [draftVehicle, setDraftVehicle] = useState(null);
  const [appliedVehicle, setAppliedVehicle] = useState(null);
  const [draftFrom, setDraftFrom] = useState(startOfMonthYmd());
  const [draftTo, setDraftTo] = useState(toYmd(new Date()));
  const [appliedFrom, setAppliedFrom] = useState(startOfMonthYmd());
  const [appliedTo, setAppliedTo] = useState(toYmd(new Date()));
  const [formData, setFormData] = useState({
    vehicle: null,
    type: "",
    description: "",
    date: "",
    amount: "",
    quantity: "",
    paymentMode: "",
    file: null,
  });

  useEffect(() => {
    setPage(1);
  }, [mode, appliedVehicle, appliedFrom, appliedTo, rowsPerPage]);

  const filteredRows = useMemo(() => {
    const fromTs = appliedFrom ? new Date(`${appliedFrom}T00:00:00`).getTime() : 0;
    const toTs = appliedTo ? new Date(`${appliedTo}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER;
    return expenses.filter((row) => {
      const rowTs = new Date(row.date).getTime();
      if (appliedVehicle && String(row.vehicleId) !== String(appliedVehicle._id)) return false;
      if (Number.isFinite(rowTs) && (rowTs < fromTs || rowTs > toTs)) return false;
      return true;
    });
  }, [appliedFrom, appliedTo, appliedVehicle, expenses]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const monthlyChartData = useMemo(() => {
    const bucket = new Map();
    filteredRows.forEach((row) => {
      const date = new Date(row.date);
      if (Number.isNaN(date.getTime())) return;
      const key = date.toLocaleString("en-IN", { month: "short" });
      bucket.set(key, (bucket.get(key) || 0) + Number(row.amount || 0));
    });
    return Array.from(bucket.entries()).map(([name, amount]) => ({ name, amount }));
  }, [filteredRows]);

  const typeChartData = useMemo(() => {
    const bucket = new Map();
    filteredRows.forEach((row) => {
      const key = row.type || "Other";
      bucket.set(key, (bucket.get(key) || 0) + Number(row.amount || 0));
    });
    return Array.from(bucket.entries()).map(([name, value]) => ({ name, value }));
  }, [filteredRows]);

  const handleApplyFilters = () => {
    setAppliedVehicle(draftVehicle);
    setAppliedFrom(draftFrom);
    setAppliedTo(draftTo);
    setPage(1);
  };

  const handleDownload = () => {
    const csv = buildCsv(filteredRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expenses-${appliedFrom || "from"}-${appliedTo || "to"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showStatus("success", "Expense CSV downloaded.");
  };

  const resetForm = () =>
    setFormData({
      vehicle: null,
      type: "",
      description: "",
      date: "",
      amount: "",
      quantity: "",
      paymentMode: "",
      file: null,
    });

  const handleSubmit = async () => {
    if (!formData.vehicle?._id || !formData.type || !formData.date || !formData.amount) {
      showStatus("warning", "Vehicle, type, date aur amount required hain.");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("vehicle", formData.vehicle._id);
      fd.append("type", formData.type);
      fd.append("description", formData.description || "");
      fd.append("date", formData.date);
      fd.append("amount", formData.amount);
      if (formData.quantity) fd.append("quantity", formData.quantity);
      if (formData.paymentMode) fd.append("paymentMode", formData.paymentMode);
      if (formData.file) fd.append("file", formData.file);

      await addExpense(fd).unwrap();
      await refetch();
      showStatus("success", "Expense saved.");
      resetForm();
      setMode("table");
      setPage(1);
    } catch (err) {
      showStatus("error", err?.data?.error || "Failed to save expense.");
    }
  };

  const formView = (
    <Box sx={{ px: 3, pb: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>Add Expense</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
        <Autocomplete
          options={devices}
          value={formData.vehicle}
          onChange={(_, value) => setFormData((prev) => ({ ...prev, vehicle: value }))}
          getOptionLabel={(option) => getVehicleLabel(option)}
          renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="Select Vehicle" sx={inputSx} />}
        />
        <FormControl fullWidth sx={inputSx}>
          <InputLabel>Type</InputLabel>
          <Select
            label="Type"
            value={formData.type}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value }))}
          >
            {EXPENSE_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          sx={inputSx}
        />
        <TextField
          type="date"
          label="Date"
          InputLabelProps={{ shrink: true }}
          value={formData.date}
          onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
          sx={inputSx}
        />
        <TextField
          label="Amount"
          type="number"
          value={formData.amount}
          onChange={(e) => setFormData((prev) => ({ ...prev, amount: e.target.value }))}
          sx={inputSx}
        />
        <TextField
          label="Quantity"
          type="number"
          value={formData.quantity}
          onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
          sx={inputSx}
        />
        <TextField
          type="file"
          inputProps={{ accept: "image/*,.pdf" }}
          onChange={(e) => setFormData((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
          sx={inputSx}
        />
        <FormControl fullWidth sx={inputSx}>
          <InputLabel>Mode of payment</InputLabel>
          <Select
            label="Mode of payment"
            value={formData.paymentMode}
            onChange={(e) => setFormData((prev) => ({ ...prev, paymentMode: e.target.value }))}
          >
            {PAYMENT_MODES.map((modeValue) => (
              <MenuItem key={modeValue} value={modeValue}>
                {modeValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ minWidth: 160, bgcolor: "#ff5177", "&:hover": { bgcolor: "#f43f5e" } }}
        >
          {isSaving ? "Saving..." : "SUBMIT"}
        </Button>
        <Button
          variant="contained"
          onClick={() => setMode("table")}
          sx={{ minWidth: 160, bgcolor: "#6b7280", "&:hover": { bgcolor: "#4b5563" } }}
        >
          CANCEL
        </Button>
      </Stack>
    </Box>
  );

  const tableView = (
    <>
      <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2.2fr 1.2fr 1.2fr auto" }, gap: 2, alignItems: "center" }}>
          <Autocomplete
            options={devices}
            value={draftVehicle}
            onChange={(_, value) => setDraftVehicle(value)}
            getOptionLabel={(option) => getVehicleLabel(option)}
            renderInput={(params) => <TextField {...params} placeholder="Select Vehicle" sx={inputSx} />}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 17 }}>From</Typography>
            <TextField type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} sx={inputSx} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 17 }}>To</Typography>
            <TextField type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} sx={inputSx} />
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={handleApplyFilters} sx={{ border: "1px solid #fecdd3", borderRadius: 1.25, color: "#fb7185" }}>
              <SearchIcon />
            </IconButton>
            <IconButton onClick={() => setMode("form")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
              <AddIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 88 }}>
          <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
            {[10, 25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={() => setMode("chart")} sx={{ color: "#374151" }}>
            <ChartIcon />
          </IconButton>
          <IconButton onClick={handleDownload} sx={{ color: "#6b7280" }}>
            <DownloadIcon />
          </IconButton>
        </Stack>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ maxHeight: "56vh", borderRadius: 0 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {["Date", "Vehicle no", "Odometer", "Type", "Qty", "Mileage", "Model", "Amount", "Average", "Payment mode", "Description", "Image"].map((head) => (
                <TableCell key={head} sx={{ fontWeight: 700, color: "#4b5563", bgcolor: "#fff", borderBottom: "1px solid #d1d5db" }}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedRows.length ? (
              pagedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{row.dateLabel}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{row.vehicleNo}</TableCell>
                  <TableCell>{row.odometer ? row.odometer.toLocaleString() : "-"}</TableCell>
                  <TableCell>{String(row.type || "").toLowerCase()}</TableCell>
                  <TableCell>{row.quantity || "-"}</TableCell>
                  <TableCell>{row.mileage || "-"}</TableCell>
                  <TableCell>{row.model}</TableCell>
                  <TableCell>{row.amount}</TableCell>
                  <TableCell>{row.average || 0}</TableCell>
                  <TableCell>{String(row.paymentMode || "").toLowerCase()}</TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>
                    {row.image ? (
                      <Link href={row.image} target="_blank" rel="noreferrer">
                        Image
                      </Link>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6, color: "#6b7280" }}>
                  No expense found for selected filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ color: "#374151" }}>
          Showing {filteredRows.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
        </Typography>
        <Pagination
          page={page}
          count={totalPages}
          onChange={(_, value) => setPage(value)}
          shape="rounded"
        />
      </Box>
    </>
  );

  const chartView = (
    <>
      <Box sx={{ px: 3, py: 2.5, borderBottom: "1px solid #e5e7eb" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2.2fr 1.2fr 1.2fr auto" }, gap: 2, alignItems: "center" }}>
          <Autocomplete
            options={devices}
            value={draftVehicle}
            onChange={(_, value) => setDraftVehicle(value)}
            getOptionLabel={(option) => getVehicleLabel(option)}
            renderInput={(params) => <TextField {...params} placeholder="Select Vehicle" sx={inputSx} />}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 17 }}>From</Typography>
            <TextField type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} sx={inputSx} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 17 }}>To</Typography>
            <TextField type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} sx={inputSx} />
          </Box>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton onClick={handleApplyFilters} sx={{ border: "1px solid #fecdd3", borderRadius: 1.25, color: "#fb7185" }}>
              <SearchIcon />
            </IconButton>
            <IconButton onClick={() => setMode("form")} sx={{ border: "2px solid #111827", borderRadius: 0.5, color: "#111827" }}>
              <AddIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>
      <Box sx={{ px: 3, py: 1.5, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <IconButton onClick={() => setMode("table")} sx={{ color: "#374151" }}>
          <TableRowsIcon />
        </IconButton>
        <IconButton onClick={handleDownload} sx={{ color: "#6b7280" }}>
          <DownloadIcon />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, py: 3, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, minHeight: 440 }}>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1.5 }}>Expense by Month</Typography>
          <ResponsiveContainer width="100%" height={340}>
            <LineChart data={monthlyChartData}>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="amount" stroke="#4f9cf9" strokeWidth={4} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 1.5 }}>Expense By Type</Typography>
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie data={typeChartData} dataKey="value" nameKey="name" innerRadius={85} outerRadius={125} paddingAngle={2}>
                {typeChartData.map((entry, index) => (
                  <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", minHeight: "78vh" } }}>
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ borderTop: "4px solid #4f9cf9", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800 }}>Expense</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {mode === "form" ? formView : mode === "chart" ? chartView : tableView}
      </DialogContent>
    </Dialog>
  );
};

export default ExpensePortalModal;
